// ─────────────────────────────────────────────────────────────────────────────
// deltaRunner.ts — WHAT DELTA IS ACTUALLY DOING. Buck's shape, with a terminal.
//
// `GET /api/delta/runner` · `POST /api/delta/runner`. Forge owns both; this is
// the client and it degrades by name until they land.
//
// ── BUCK'S RULINGS CARRY OVER UNCHANGED ────────────────────────────────────
// From `runControl.ts`, and every one of them was paid for:
//   • OFF IS ALWAYS SENDABLE, ON IS NOT. Stopping something already stopped is a
//     no-op; arming without a read is arming something we cannot describe.
//   • NEVER INVENT ON. An unread state renders unread — never OFF, never idle.
//   • `sending` IS A TRANSPORT FACT about the request, never a claim about the
//     agent. The control does not move because the click left.
//   • A FAILED WRITE RE-READS. It never keeps the value that was clicked.
//   • A CONTROL ANSWERS IN A FEW WORDS and does not explain its own
//     epistemology. The vocabulary is RUN · STOP · UNREAD · RETRYING and now
//     COMPLETE, and nothing else reaches a screen.
//
// ── THE ONE DIFFERENCE, AND IT NEEDS A WORD BUCK HAS NO USE FOR ────────────
// BUCK'S WALK NEVER ENDS. DELTA'S RUN IS PER-COMPANY AND IT TERMINATES — at the
// script, on the Calls tab. Without a terminal state, "finished the script" and
// "never started" are the same absence, and the owner cannot tell a completed
// run from a dead one. So the producer says `complete`, carrying the company it
// completed, and this client refuses to derive it: a state derived from silence
// cannot be explained in the producer's own words.
//
// ── `idle` IS A PRODUCER STATE AND IT ARRIVES WITH A DEPTH ─────────────────
// "Delta works the queue and returns to idle between companies." An idle runner
// with four companies queued and an idle runner with none are DIFFERENT FACTS.
// `queueDepth` is read from the KEY, not inferred from the value — a build that
// does not publish the depth says so (`depthKnown: false`) rather than showing a
// confident zero.
// ─────────────────────────────────────────────────────────────────────────────

import { apiBase, authToken } from './apiBase';

export const DELTA_RUNNER_PATH = '/delta/runner';

/** The producer's word. `unread` is not a state Delta is in — it is a state WE
 *  are in about Delta, and it is first in the union so nothing defaults past it. */
// `stopped` (owner ruling 2026-09-14): a run that ENDED SHORT OF THE SCRIPT.
// `complete` is the script existing. A run stopped at research_incomplete (no CLI
// session, lanes never asked) used to publish `complete`, and the screen said
// COMPLETE over a run that had asked nothing. The producer says `stopped`, and
// its `detail` names the lanes; this client does not derive either.
export type DeltaRunState = 'unread' | 'absent' | 'idle' | 'running' | 'complete' | 'stopped' | 'failed';

export interface DeltaRunnerState {
  state: DeltaRunState;
  /** The company the runner is on, or the one it just completed. */
  companyId: string | null;
  queueDepth: number | null;
  /** Whether the build published a depth AT ALL. Read the key, not the value. */
  depthKnown: boolean;
  /** The producer's own sentence when it has one. Never composed here. */
  detail: string;
  // ── PUBLISHED BEYOND THE FIRST CONTRACT, READ BY KEY (the run control's
  //    dropdown, owner ruling 2026-09-14). Optional: absent means this client
  //    was handed a state without them, and nothing is invented in their place.
  /** The stacked company ids, in order. Null when the key is absent. */
  queue?: string[] | null;
  /** The lane being asked, while running. */
  lane?: string | null;
  startedAt?: string | null;
  /** The last run's own stop sentence. */
  lastStopped?: string | null;
  /** THE OVERNIGHT LOOP, as the producer names it on this payload (owner ruling
   *  2026-09-17). Null when the key is absent — never read as "the loop is off". */
  loop?: DeltaLoopState | null;
  /** A command the route REFUSED, in the producer's own sentence. */
  refusal?: string | null;
}

export interface DeltaLoopStop {
  kind: string;
  reason: string;
  at: string | null;
  business: string | null;
  summary: string | null;
  thisRun: string | null;
}

export interface DeltaLoopState {
  armed: boolean;
  /** Disarmed, with the company in flight still finishing its chain. */
  finishing: boolean;
  companyId: string | null;
  business: string | null;
  /** "8 scripted · 0 parked · 5 need a website · 124 left of 137" — AGAINST THE
   *  HANDOFF (owner ruling 2026-09-17), the same population the Sales tiles count. */
  summary: string | null;
  /** "7 this run" — the arming's own count, said beneath the handoff line. */
  thisRun: string | null;
  /** Why the loop last stopped, read back across a boot. */
  lastStop: DeltaLoopStop | null;
}

export const RUNNER_UNREAD: DeltaRunnerState = {
  state: 'unread', companyId: null, queueDepth: null, depthKnown: false, detail: '',
};

const STATES: DeltaRunState[] = ['idle', 'running', 'complete', 'stopped', 'failed'];

export async function readRunner(): Promise<DeltaRunnerState> {
  let res: Response;
  try {
    res = await fetch(apiBase() + DELTA_RUNNER_PATH, {
      headers: { Authorization: 'Bearer ' + authToken() },
    });
  } catch {
    return RUNNER_UNREAD;
  }
  if (res.status === 404) {
    return { ...RUNNER_UNREAD, state: 'absent', detail: 'this build does not publish a Delta runner' };
  }
  if (!res.ok) return RUNNER_UNREAD;
  let body: any;
  try {
    body = await res.json();
  } catch {
    return RUNNER_UNREAD;
  }
  return normalizeDeltaRunner(body);
}

function ownText(d: any, key: string): string | null {
  if (!d || typeof d !== 'object' || !Object.prototype.hasOwnProperty.call(d, key)) return null;
  const v = d[key];
  return typeof v === 'string' && v.trim() ? v.trim() : null;
}

function normalizeLoop(body: any): DeltaLoopState | null {
  const l = body && Object.prototype.hasOwnProperty.call(body, 'loop') ? body.loop : undefined;
  if (!l || typeof l !== 'object' || Array.isArray(l) || typeof l.armed !== 'boolean') return null;
  const s = l.last_stop && typeof l.last_stop === 'object' && !Array.isArray(l.last_stop) ? l.last_stop : null;
  const reason = ownText(s, 'reason');
  return {
    armed: l.armed,
    finishing: l.finishing === true,
    companyId: ownText(l, 'company_id'),
    business: ownText(l, 'business'),
    summary: ownText(l, 'summary'),
    thisRun: ownText(l, 'this_run'),
    lastStop: s && reason ? {
      kind: ownText(s, 'kind') || '', reason, at: ownText(s, 'at'),
      business: ownText(s, 'business'), summary: ownText(s, 'summary'),
      thisRun: ownText(s, 'this_run'),
    } : null,
  };
}

/** The wire body → state. Pure, so the refusal to map is provable without a fetch. */
export function normalizeDeltaRunner(body: any): DeltaRunnerState {
  const word = String(body?.state || '').trim() as DeltaRunState;
  // A WORD THIS CLIENT DOES NOT KNOW IS UNREAD, NOT A GUESS. A producer that
  // invents a fifth state must be read as unreadable rather than mapped onto
  // the nearest one — the mapping is where a wrong render becomes confident.
  if (STATES.indexOf(word) < 0) return RUNNER_UNREAD;
  const depthKnown = Object.prototype.hasOwnProperty.call(body || {}, 'queue_depth');
  const rawDepth = body?.queue_depth;
  const queueKnown = !!body && Object.prototype.hasOwnProperty.call(body, 'queue') && Array.isArray(body.queue);
  const last = body && typeof body.last === 'object' && !Array.isArray(body.last) ? body.last : null;
  return {
    state: word,
    companyId: String(body?.company_id || '').trim() || null,
    queueDepth: depthKnown && typeof rawDepth === 'number' && Number.isFinite(rawDepth)
      ? rawDepth : null,
    depthKnown,
    detail: String(body?.detail || '').trim(),
    queue: queueKnown ? body.queue.map((x: unknown) => String(x)).filter((x: string) => x.trim()) : null,
    lane: ownText(body, 'lane'),
    startedAt: ownText(body, 'started_at'),
    lastStopped: ownText(last, 'stopped'),
    loop: normalizeLoop(body),
    refusal: ownText(body, 'refused'),
  };
}

export interface RunCommand {
  action: 'run' | 'stop';
  companyId?: string | null;
}

/** Send the command. Returns the state the route answers with, or `unread` —
 *  and the caller RE-READS on failure rather than keeping what was clicked. */
export async function sendRunner(cmd: RunCommand): Promise<DeltaRunnerState> {
  let res: Response;
  try {
    res = await fetch(apiBase() + DELTA_RUNNER_PATH, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + authToken() },
      body: JSON.stringify({ action: cmd.action, company_id: cmd.companyId || null }),
    });
  } catch {
    return RUNNER_UNREAD;
  }
  // A REFUSAL IS AN ANSWER, NOT A FAILED WRITE. The route refuses a manual run
  // while the loop holds the box and says why, with the state beside it — that
  // body IS the read, so its sentence reaches the control instead of an UNREAD.
  if (res.status === 409) {
    try {
      const body = await res.json();
      const refused = ownText(body, 'refused');
      if (refused) return { ...normalizeDeltaRunner(body), refusal: refused };
    } catch {
      return readRunner();
    }
    return readRunner();
  }
  if (!res.ok) return RUNNER_UNREAD;
  return readRunner();
}

/** ARMING NEEDS A READ; STOPPING NEVER DOES. Buck's asymmetry, unchanged: a stop
 *  starts nothing and spends nothing, so it is offered even while unread. */
export function canRun(state: DeltaRunState, hasCompany: boolean): boolean {
  return hasCompany && (state === 'idle' || state === 'complete' || state === 'stopped' || state === 'failed');
}

export function canStop(state: DeltaRunState): boolean {
  return state !== 'absent';
}

/** The whole vocabulary that reaches a screen. */
export function runnerCaption(s: DeltaRunnerState): string {
  switch (s.state) {
    case 'running': return 'RUNNING';
    case 'complete': return 'COMPLETE';
    case 'stopped': return 'STOPPED';
    case 'failed': return 'FAILED';
    case 'idle': return s.depthKnown && s.queueDepth ? 'IDLE · ' + s.queueDepth + ' QUEUED' : 'IDLE';
    case 'absent': return 'NO RUNNER IN THIS BUILD';
    default: return 'UNREAD';
  }
}
